// COS-SLICE1 (docs/design/2026-10-28-chief-of-staff-and-assistants.md §2.5, §5, §9.1): the Chief
// of Staff's loop settings (Setup › Workforce › Chief of Staff: one small form) and the follow-up
// cards under chat messages ("Make this a task?", "Set a reminder?", "Schedule a meeting?").
// A card is accepted by the person who clicks, through a modal prefilled for review, and it
// creates the item as that person; nothing is created silently, nothing by the agent.
import { z } from "zod";
import { SuggestionKind, SuggestionResultKind, SuggestionSource, SuggestionState } from "../enums";
import { LIMITS, Timestamp, text, Uuid } from "../primitives";
import { CosQuietHours } from "../rows";
import { CompanyItemParams, CompanyParams } from "./common";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";

// Chief of Staff settings --------------------------------------------------------------------------

/** The loop's settings as they apply (the stored company setting over the defaults). */
export const CosSettings = z
  .strictObject({
    interval_min: z.int().min(5).max(60),
    max_runs_per_hour: z.int().min(1).max(60),
    max_runs_per_day: z.int().min(1).max(500),
    /** Null: no quiet hours. */
    quiet_hours: CosQuietHours.nullable(),
    approval_wait_hours: z.int().min(1).max(72),
    max_effects_per_run: z.int().min(1).max(100),
    suggestions: z.boolean(),
  })
  .meta({ id: "CosSettings" });
export type CosSettings = z.infer<typeof CosSettings>;

/** One DE that may carry the digest: whether it does, where it is posted, and its recent use. */
export const CosDigestDe = z
  .object({
    membership_id: Uuid,
    name: z.string(),
    enabled: z.boolean(),
    /** Where the digest is posted; null: the DE's first open channel (`#general` first). */
    channel_id: Uuid.nullable(),
    /** The non-DM channels it is a member of (the choices for `channel_id`). */
    channels: z.array(z.object({ id: Uuid, name: z.string(), is_private: z.boolean() })),
    last_check_at: Timestamp.nullable(),
    last_digest_at: Timestamp.nullable(),
    last_digest_read_at: Timestamp.nullable(),
    runs_last_hour: z.int(),
    runs_last_day: z.int(),
  })
  .meta({ id: "CosDigestDe" });

export const CosView = z
  .object({ settings: CosSettings, des: z.array(CosDigestDe) })
  .meta({ id: "CosView" });
export type CosView = z.infer<typeof CosView>;

export const CosPut = z
  .strictObject({
    settings: CosSettings,
    /** The DEs whose digest changes; a DE not listed keeps what it has. */
    des: z
      .array(
        z.strictObject({ membership_id: Uuid, enabled: z.boolean(), channel_id: Uuid.nullable() }),
      )
      .max(50),
  })
  .meta({ id: "CosPut" });
export type CosPut = z.infer<typeof CosPut>;

// Suggestions --------------------------------------------------------------------------------------

export const SuggestionProposalView = z
  .object({ title: z.string(), at: Timestamp, span: z.string(), trigger: z.string() })
  .meta({ id: "SuggestionProposal" });

export const Suggestion = z
  .object({
    id: Uuid,
    message_id: Uuid,
    thread_id: Uuid,
    channel_id: Uuid,
    kind: SuggestionKind,
    state: SuggestionState,
    source: SuggestionSource,
    proposal: SuggestionProposalView,
    /** The Chief of Staff the card is written as. */
    created_by_membership_id: Uuid,
    decided_by_membership_id: Uuid.nullable(),
    decided_at: Timestamp.nullable(),
    result_kind: SuggestionResultKind.nullable(),
    result_id: Uuid.nullable(),
    expires_at: Timestamp,
    created_at: Timestamp,
    /** The modal's defaults: the channel's board, and the team's meeting series (meeting cards). */
    defaults: z.object({ board_id: Uuid.nullable(), meeting_series_id: Uuid.nullable() }),
  })
  .meta({ id: "Suggestion" });
export type Suggestion = z.infer<typeof Suggestion>;

/** What the person reviewed in the modal; the kind must be the card's. */
export const SuggestionAccept = z
  .discriminatedUnion("kind", [
    z.strictObject({
      kind: z.literal("task"),
      title: text(LIMITS.name),
      due_at: Timestamp.nullable(),
      board_id: Uuid,
      assignee_seat_ids: z.array(Uuid).max(10).optional(),
    }),
    z.strictObject({
      kind: z.literal("reminder"),
      text: text(2000),
      at: Timestamp,
      /** A seat the person holds; default: their first. */
      seat_id: Uuid.optional(),
    }),
    z.strictObject({
      kind: z.literal("meeting"),
      series_id: Uuid,
      scheduled_at: Timestamp,
    }),
  ])
  .meta({ id: "SuggestionAccept" });
export type SuggestionAccept = z.infer<typeof SuggestionAccept>;

export const cosRoutes = {
  "chiefOfStaff.get": defineRoute({
    id: "chiefOfStaff.get",
    feature: "agents",
    tag: "chief-of-staff",
    method: "get",
    path: `${base}/chief-of-staff`,
    summary: "The Chief of Staff loop: interval, caps, quiet hours, and which DE has the digest",
    access: "manager",
    params: CompanyParams,
    response: { status: 200, schema: CosView },
    errors: [],
    emits: [],
  }),
  "chiefOfStaff.put": defineRoute({
    id: "chiefOfStaff.put",
    feature: "agents",
    tag: "chief-of-staff",
    method: "put",
    path: `${base}/chief-of-staff`,
    summary: "Set the loop's interval, caps and quiet hours, and turn the digest on or off per DE",
    access: "manager",
    params: CompanyParams,
    body: CosPut,
    response: { status: 200, schema: CosView },
    errors: [],
    emits: ["company.updated", "agent_profile.updated"],
  }),
  "suggestions.list": defineRoute({
    id: "suggestions.list",
    feature: "chat",
    tag: "messages",
    method: "get",
    path: `${base}/threads/{id}/suggestions`,
    summary: "The pending follow-up cards under this thread's messages (people only)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: z.object({ data: z.array(Suggestion) }) },
    errors: [],
    emits: [],
  }),
  "suggestions.accept": defineRoute({
    id: "suggestions.accept",
    feature: "chat",
    tag: "messages",
    method: "post",
    path: `${base}/suggestions/{id}/accept`,
    summary: "Accept a follow-up card: create the reviewed task, reminder or meeting as me",
    access: "member",
    params: CompanyItemParams,
    body: SuggestionAccept,
    idempotency: "optional",
    response: { status: 200, schema: Suggestion },
    errors: ["suggestion_decided", "no_seat", "conflict"],
    emits: ["suggestion.accepted", "task.created", "reminder.created", "meeting.scheduled"],
  }),
  "suggestions.dismiss": defineRoute({
    id: "suggestions.dismiss",
    feature: "chat",
    tag: "messages",
    method: "post",
    path: `${base}/suggestions/{id}/dismiss`,
    summary: "Dismiss a follow-up card (it is never raised again for that message)",
    access: "member",
    params: CompanyItemParams,
    response: { status: 200, schema: Suggestion },
    errors: ["suggestion_decided"],
    emits: ["suggestion.dismissed"],
  }),
} as const;
